import {
  suggestAddresses,
  createSessionToken,
  resolveLocation,
  init as initLanesSDK,
  type AddressSuggestion,
  type SuggestAddressesParams,
  type ResolveLocationResult,
} from '@hopdrive/sdk-lanes';
import { useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { gqlExecutor } from '@apollolocal/client';
import siteConfig from '@config';

// sdk-lanes' geo functions (suggest/resolve/geocode) need their own init beyond
// the core executor: the Google API key + geo providers. Every geo caller in this
// app goes through this hook, so it inits here. Idempotent same-value write — if
// you init sdk-lanes anywhere else, pass the SAME config (see docs/patterns/codegen.md).
if (siteConfig.googleApiKey) {
  initLanesSDK({ executor: gqlExecutor, googleApiKey: siteConfig.googleApiKey });
}

interface UseAddressSuggestionsOptions {
  includedRegionCodes?: string[];
  locationBias?: { latitude: number; longitude: number; radiusMeters?: number };
}

export function useAddressSuggestions(options?: UseAddressSuggestionsOptions) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(
    (input: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!input || input.length < 3) {
        setSuggestions([]);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = createSessionToken();
        }

        setLoading(true);
        try {
          const result = await suggestAddresses({
            input,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: options?.includedRegionCodes ?? ['us'],
            locationBias: options?.locationBias,
          });
          setSuggestions(result.predictions);
        } catch (err) {
          // Degrade to an empty list for the user, but do NOT swallow the reason:
          // a Google Places key/referrer/quota failure (e.g. a 403) otherwise looks
          // identical to "no matches", leaving a silently dead address field with no
          // signal anywhere. Report it so the break is observable.
          console.error('Address suggestion failed:', err);
          Sentry.captureException(err, { tags: { feature: 'address-suggestions', action: 'suggest' } });
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [options?.includedRegionCodes, options?.locationBias]
  );

  const select = useCallback(async (suggestion: AddressSuggestion, customerId: number): Promise<ResolveLocationResult> => {
    sessionTokenRef.current = null;
    setSuggestions([]);

    return resolveLocation({
      address: suggestion.fullText,
      customerId,
      name: suggestion.mainText,
      placeId: suggestion.placeId,
    });
  }, []);

  const resolve = useCallback(async (address: string, customerId: number): Promise<ResolveLocationResult> => {
    sessionTokenRef.current = null;
    setSuggestions([]);

    return resolveLocation({
      address,
      customerId,
    });
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    sessionTokenRef.current = null;
    setSuggestions([]);
  }, []);

  return { suggestions, loading, search, select, resolve, clear };
}

export type { AddressSuggestion };
